import * as React from 'react';
import {
    focusAsync,
    getId,
    classNamesFunction,
    getNativeProps,
    divProperties,
    IPopupRestoreFocusParams,
    getWindow,
} from '@fluentui/react';
import {
    IGroupedComboBoxProps,
    IGroupedComboBoxStyles,
    IGroupedComboBoxStyleProps,
} from './GroupedComboBox.types';
import { GroupedComboBoxDropDown } from '../GroupedComboBoxDropDown/GroupedComboBoxDropDown';
import {
    GroupedComboBoxOnChangeProps,
    IGroupedComboBoxDropDown,
    ISelectedItem,
    isKeyboardEvent,
    IGroupedComboBoxItem,
    GroupStates,
    usePendingChange,
} from 'grouped-combobox-core';
import { TextFieldWithAction, ITextFieldWithAction, assert } from 'textfield-with-action';
import { useBoolean, useConst } from '@fluentui/react-hooks';
import { moveFocusToNextElement } from '../utils/moveFocusToNextElement';

const getClassNames = classNamesFunction<IGroupedComboBoxStyleProps, IGroupedComboBoxStyles>();

/**
 * The React GroupedComboBox control.
 * @param props - The props passed to this control when rendered.
 */
export const GroupedComboBoxBase = React.forwardRef<HTMLDivElement, IGroupedComboBoxProps>(
    (props: IGroupedComboBoxProps, ref): JSX.Element => {
        const {
            id = getId('GroupedComboBox'),
            onItemClick,
            componentRef,
            onMenuOpened,
            onMenuDismissed,
            onFilterItems,
            onFilterEnd,
            onFilterBegin,
            enableFiltering,
            disabled,
            onChange,
            tabIndex,
            onRenderTextFieldPrefix,
            iconResources,
            ariaLabel,
            styles,
            theme,
            menuInitiallyOpened,
            hideFocusVisual,
            onItemGotFocus,
            onItemPreview,
            width,
            keytipProps,
            selectedItem,
            maxTextLength,
            ...rest
        } = props;

        assert(theme);
        const processedStyles = getClassNames(styles, {
            theme,
            width,
        });

        const [currentItem, setCurrentItem] = React.useState(selectedItem);
        const [currentFilter, setCurrentFilter] = React.useState<string>();
        const [isDropDownOpen, setIsDropDownOpen] = useBoolean(menuInitiallyOpened ?? false);
        const [menuHasFocus, setMenuHasFocus] = React.useState(false);

        // This constant stores the collapse states of all groups throughout the lifecycle of this control.
        const groupStates = useConst<GroupStates>(() => new Map<string, boolean>());
        const isFiltering = currentFilter !== undefined;

        // This ref's purpose is to avoid the below callbacks taking dependency
        // on `isDropDownOpen` directly, which would invalidate the callbacks too often.
        const isOpened = React.useRef<boolean>(false);
        isOpened.current = isDropDownOpen;

        const handleMoveFocusWhenMenuClose = React.useRef<boolean>(false);

        const textFieldWithAction = React.useRef<ITextFieldWithAction>(null);

        const openDropDownCallback = React.useCallback(() => {
            onMenuOpened?.();
            handleMoveFocusWhenMenuClose.current = false;
            setIsDropDownOpen.setTrue();
        }, [onMenuOpened, setIsDropDownOpen]);

        const onPendingChangeCallback = usePendingChange(
            enableFiltering,
            isOpened,
            openDropDownCallback,
            onFilterItems || setCurrentFilter,
            onFilterBegin
        );

        const closeDropDownCallback = React.useCallback(
            (doNotCancelPendingText: boolean) => {
                setCurrentFilter(undefined);

                // cancel any pending timer which could potentially trigger filtering after menu has closed.
                onPendingChangeCallback(null);
                if (isFiltering) {
                    onFilterEnd?.();
                }
                if (!doNotCancelPendingText) {
                    textFieldWithAction.current?.cancelPendingChange(false);
                }

                // only raise the onMenuDismissed event if the dropdown is currently open.
                if (isOpened.current) {
                    setIsDropDownOpen.setFalse();

                    if (onMenuDismissed) {
                        window.requestAnimationFrame(onMenuDismissed);
                    }
                }

                setMenuHasFocus(false);
            },
            [
                onMenuDismissed,
                setCurrentFilter,
                setIsDropDownOpen,
                textFieldWithAction,
                onFilterEnd,
                onPendingChangeCallback,
                isFiltering,
            ]
        );

        const dropDownToggle = React.useCallback(() => {
            if (isOpened.current) {
                closeDropDownCallback(false);
            } else {
                openDropDownCallback();
            }
        }, [closeDropDownCallback, openDropDownCallback]);

        React.useEffect(() => {
            selectedItem !== undefined && setCurrentItem(selectedItem);
        }, [selectedItem, setCurrentItem]);

        const itemClickCallback = React.useCallback(
            (
                item: IGroupedComboBoxItem,
                ev?: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>
            ) => {
                if (ev && isKeyboardEvent(ev) && ev.key === 'Tab') {
                    // If user presses Tab to select an item, we want to handle moving focus to the next element after
                    // this control, rather than after the container Layer of the dropdown.
                    ev.preventDefault();

                    handleMoveFocusWhenMenuClose.current = true;
                }

                // If this item click sets the item, the pending text will be updated anyway
                // so close the dropdown without keeping the pending text
                closeDropDownCallback(false);

                onItemClick?.(item, ev);
                ev &&
                    !ev.defaultPrevented &&
                    setCurrentItem({
                        name: item.text,
                        key: item.itemKey,
                    });
            },
            [onItemClick, setCurrentItem, closeDropDownCallback]
        );

        const itemGotFocusCallback = React.useCallback(
            (item: IGroupedComboBoxItem, ev?: React.FocusEvent<unknown>) => {
                onItemGotFocus && onItemGotFocus(item, ev);
                const text = onItemPreview ? onItemPreview(item, ev) : item.text;
                if (!ev?.defaultPrevented && text) {
                    textFieldWithAction.current?.setPendingText(text);
                }
                setMenuHasFocus(true);
            },
            [onItemGotFocus, onItemPreview]
        );

        const onDismissCallback = React.useCallback(() => {
            closeDropDownCallback(false);
        }, [closeDropDownCallback]);

        const dropDown = React.useRef<IGroupedComboBoxDropDown>(null);

        React.useImperativeHandle(
            componentRef,
            () => ({
                announceText: (message: string): void => {
                    dropDown.current?.announceText(message);
                },
                dismissMenu: onDismissCallback,
                focus: (shouldOpenOnFocus, useFocusAsync): boolean => {
                    if (!textFieldWithAction.current?.focus) {
                        return false;
                    }
                    useFocusAsync
                        ? focusAsync(textFieldWithAction.current)
                        : textFieldWithAction.current.focus();
                    if (shouldOpenOnFocus) {
                        openDropDownCallback();
                    }
                    return true;
                },
                get isDropdownOpen(): boolean {
                    return isOpened.current;
                },
            }),
            [textFieldWithAction, onDismissCallback, openDropDownCallback]
        );

        const onKeyDown = React.useCallback(
            (ev: React.KeyboardEvent<HTMLInputElement>) => {
                switch (ev.key) {
                    case 'ArrowDown':
                        if (ev.altKey) {
                            // Alt + Down opens the drop down
                            if (!isOpened.current) {
                                openDropDownCallback();
                                ev.stopPropagation();
                                ev.preventDefault();
                            }
                        } else if (isOpened.current) {
                            // Down alone moves focus to the drop down
                            dropDown.current?.focusDropDown();
                            ev.stopPropagation();
                            ev.preventDefault();
                        }
                        break;

                    case 'ArrowUp':
                        // Alt + Up closes the dropdown if it opens
                        if (ev.altKey && isOpened.current) {
                            closeDropDownCallback(false);
                            ev.stopPropagation();
                            ev.preventDefault();
                        }
                        break;
                }
            },
            [openDropDownCallback, closeDropDownCallback]
        );

        const onCancelCallback = React.useCallback(
            (ev: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                if (isOpened.current) {
                    closeDropDownCallback(false);
                    ev.stopPropagation();
                    ev.preventDefault();
                }
            },
            [closeDropDownCallback]
        );

        const onSubmitCallback = React.useCallback(
            (ev: React.KeyboardEvent<HTMLInputElement>, value?: string, selectedValue?: string) => {
                // If the input text hasn't changed, or if it is empty, and the Enter key is pressed,
                // we restore the selected text and open the dropdown.
                if (
                    ev.key === 'Enter' &&
                    !isOpened.current &&
                    (value === '' || selectedValue === value)
                ) {
                    textFieldWithAction.current?.cancelPendingChange(false);
                    openDropDownCallback();

                    ev.stopPropagation();
                    return;
                }

                const isTabKey = ev.key === 'Tab';
                if (!value) {
                    if (isTabKey && isOpened.current) {
                        // if value is empty, just close the dropdown and do nothing else
                        closeDropDownCallback(false);
                    }

                    return;
                }

                closeDropDownCallback(true);

                if (selectedValue === undefined || selectedValue !== value) {
                    let selectedItem: ISelectedItem | undefined = undefined;
                    if (onChange) {
                        const onChangeProps: GroupedComboBoxOnChangeProps = {
                            text: value,
                        };
                        onChange(onChangeProps, ev);

                        // The handler can provide a value for the `selectedItem`, which will replace what user typed.
                        selectedItem = onChangeProps.selectedItem;
                    }

                    setCurrentItem(selectedItem ?? { name: value });
                }

                // we don't want to call stopPropagation() for Tab because
                // we want focus to move to the next control
                if (!isTabKey) {
                    ev.stopPropagation();
                }
            },
            [onChange, setCurrentItem, closeDropDownCallback, openDropDownCallback]
        );

        const onRestoreFocusCallback = React.useCallback(
            (params: IPopupRestoreFocusParams): void => {
                const { originalElement, containsFocus } = params;

                if (!containsFocus) {
                    return;
                }

                if (handleMoveFocusWhenMenuClose.current) {
                    moveFocusToNextElement(inputRef.current);
                } else if (originalElement && originalElement !== getWindow()) {
                    // This is wrapped in a setTimeout because of a React 16 bug that is resolved in 17.
                    // Once we move to 17, the setTimeout should be removed (ref: https://github.com/facebook/react/issues/17894#issuecomment-656094405)
                    setTimeout(() => {
                        originalElement.focus?.();
                    }, 0);
                }
            },
            []
        );

        const inputRef = React.useRef<HTMLInputElement>(null);

        const dropDownId = `${id}-list`;
        const divProps = getNativeProps(rest, divProperties, ['onchange', 'value']);

        return (
            <div {...divProps} id={id} ref={ref} className={processedStyles.root}>
                <TextFieldWithAction
                    ref={inputRef}
                    keytipProps={keytipProps}
                    role="combobox"
                    aria-expanded={isDropDownOpen}
                    aria-owns={isDropDownOpen ? dropDownId : undefined}
                    aria-label={ariaLabel}
                    aria-controls={isDropDownOpen ? dropDownId : undefined}
                    aria-autocomplete={enableFiltering ? 'list' : 'none'}
                    onKeyDownCapture={onKeyDown}
                    componentRef={textFieldWithAction}
                    value={currentItem?.name}
                    buttonChecked={isDropDownOpen}
                    disabled={disabled}
                    onButtonClick={dropDownToggle}
                    onSubmit={onSubmitCallback}
                    onCancel={onCancelCallback}
                    onPendingValueChanged={onPendingChangeCallback}
                    styles={processedStyles.subComponentStyles.textFieldStyles}
                    tabIndex={tabIndex}
                    onRenderPrefix={onRenderTextFieldPrefix}
                    hideFocusVisual={hideFocusVisual}
                    maxLength={maxTextLength}
                    iconProps={
                        iconResources?.dropDownButtonName
                            ? {
                                  iconName: iconResources?.dropDownButtonName,
                              }
                            : undefined
                    }
                />
                {isDropDownOpen && (
                    <GroupedComboBoxDropDown
                        {...rest}
                        id={dropDownId}
                        iconResources={iconResources}
                        componentRef={dropDown}
                        filterText={currentFilter}
                        target={inputRef}
                        onItemClick={itemClickCallback}
                        onDismiss={onDismissCallback}
                        styles={processedStyles.subComponentStyles.dropDownStyles}
                        onItemGotFocus={itemGotFocusCallback}
                        selectedKey={
                            menuHasFocus ? undefined : currentItem?.key ?? currentItem?.name
                        }
                        onRestoreFocus={onRestoreFocusCallback}
                        // Because we don't want to remember the groups states during search, we pass a
                        // new map object here. When search has finished, the temporary state is discarded
                        // and we go back to the previous state before search.
                        groupStates={isFiltering ? new Map<string, boolean>() : groupStates}
                    />
                )}
            </div>
        );
    }
);

GroupedComboBoxBase.displayName = 'GroupedComboBoxBase';
